import { csvConfigExport } from './export';
import { formatMoneyExport } from './number-helper';
import { PsrRecordType } from '@nxsys/types/models/revenueFilings/psr';
import { resolvePsrStatus } from '@nxsys/constants/psr';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';

export const onExportPSRData = (
  data: PsrRecordType[] = [],
  currencySymbol = '£',
  filename?: string,
) => {
  console.log("🚀 ~ onExportPSRData ~ filename:", filename)
  if (!data || data?.length === 0) {
    return;
  }
  const dataExport = data?.map((item) => {
    const { payload, submissionID } = item;
    const { payslips } = payload;
    const payslip = payslips?.[0];
    return {
      submissionID: submissionID,
      employeePpsn: payslip?.employeeID?.employeePpsn || '',
      employmentID: payslip?.employeeID?.employmentID || '',
      lineItemID: payslip?.lineItemID,
      firstName: payslip?.name?.firstName,
      familyName: payslip?.name?.familyName,
      payPeriod: payslip?.payPeriod,
      payFrequency: payslip?.payFrequency,
      taxCredits: formatMoneyExport(payslip?.taxCredits, currencySymbol, true),
      taxRates: payslip?.taxRates
        ?.map(
          (r) =>
            `index: ${r.index}, taxRatePercent: ${r.taxRatePercent}, yearlyRateCutOff: ${(r as any).yearlyRateCutOff || ''
            }, rateCutOff: ${r.rateCutOff || ''}`,
        )
        ?.join('\n'),
      incomeTaxCalculationBasis: payslip?.incomeTaxCalculationBasis,
      exclusionOrder: payslip?.exclusionOrder,
      payDate: payslip?.payDate
        ? moment(payslip?.payDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      grossPay: formatMoneyExport(payslip?.grossPay, currencySymbol, true),
      payForIncomeTax: formatMoneyExport(
        payslip?.payForIncomeTax,
        currencySymbol,
        true,
      ),
      incomeTaxPaid: formatMoneyExport(
        payslip?.incomeTaxPaid,
        currencySymbol,
        true,
      ),
      payForEmployeePRSI: formatMoneyExport(
        payslip?.payForEmployeePRSI,
        currencySymbol,
        true,
      ),
      payForEmployerPRSI: formatMoneyExport(
        payslip?.payForEmployerPRSI,
        currencySymbol,
        true,
      ),
      prsiExempt: payslip?.prsiExempt,
      prsiClassDetails: payslip?.prsiClassDetails
        ?.map(
          (r) =>
            `prsiClass: ${r.prsiClass}, insurableWeeks: ${r.insurableWeeks}`,
        )
        ?.join('\n'),
      employeePRSIPaid: formatMoneyExport(
        payslip?.employeePRSIPaid,
        currencySymbol,
        true,
      ),
      employerPRSIPaid: formatMoneyExport(
        payslip?.employerPRSIPaid,
        currencySymbol,
        true,
      ),
      payForUSC: formatMoneyExport(payslip?.payForUSC, currencySymbol, true),
      uscStatus: payslip?.uscStatus,
      uscPaid: formatMoneyExport(payslip?.uscPaid, currencySymbol, true),
      lptDeducted: formatMoneyExport(
        payslip?.lptDeducted,
        currencySymbol,
        true,
      ),
      grossMedicalInsurance: formatMoneyExport(
        payslip?.grossMedicalInsurance,
        currencySymbol,
        true,
      ),
      shareBasedRemuneration: formatMoneyExport(
        payslip?.shareBasedRemuneration,
        currencySymbol,
        true,
      ),
      taxableBenefits: formatMoneyExport(
        payslip?.taxableBenefits,
        currencySymbol,
        true,
      ),
      taxableLumpSum: formatMoneyExport(
        payslip?.taxableLumpSum,
        currencySymbol,
        true,
      ),
      nonTaxableLumpSum: formatMoneyExport(
        payslip?.nonTaxableLumpSum,
        currencySymbol,
        true,
      ),
      employerRBS: formatMoneyExport(
        payslip?.employerRBS,
        currencySymbol,
        true,
      ),
      employeeRBS: formatMoneyExport(
        payslip?.employeeRBS,
        currencySymbol,
        true,
      ),
      employerPRSA: formatMoneyExport(
        payslip?.employerPRSA,
        currencySymbol,
        true,
      ),
      employeePRSA: formatMoneyExport(
        payslip?.employeePRSA,
        currencySymbol,
        true,
      ),
      employeeRAC: formatMoneyExport(
        payslip?.employeeRAC,
        currencySymbol,
        true,
      ),
      employeeAVC: formatMoneyExport(
        payslip?.employeeAVC,
        currencySymbol,
        true,
      ),
      employerPEPP: formatMoneyExport(
        payslip?.employerPEPP,
        currencySymbol,
        true,
      ),
      employeePEPP: formatMoneyExport(
        payslip?.employeePEPP,
        currencySymbol,
        true,
      ),
    };
  });

  const headersExport = [
    'submissionID',
    'employeePpsn',
    'employmentID',
    'lineItemID',
    'firstName',
    'familyName',
    'payPeriod',
    'payFrequency',
    'taxCredits',
    'taxRates',
    'incomeTaxCalculationBasis',
    'exclusionOrder',
    'payDate',
    'grossPay',
    'payForIncomeTax',
    'incomeTaxPaid',
    'payForEmployeePRSI',
    'payForEmployerPRSI',
    'prsiExempt',
    'prsiClassDetails',
    'employeePRSIPaid',
    'employerPRSIPaid',
    'payForUSC',
    'uscStatus',
    'uscPaid',
    'lptDeducted',
    'grossMedicalInsurance',
    'shareBasedRemuneration',
    'taxableBenefits',
    'taxableLumpSum',
    'nonTaxableLumpSum',
    'employerRBS',
    'employeeRBS',
    'employerPRSA',
    'employeePRSA',
    'employeeRAC',
    'employeeAVC',
    'employerPEPP',
    'employeePEPP',
  ];

  const finalFilename = filename || (data.length === 1 ? `PSR-Requests-Export-${data[0]?.subcontractor?.id}-${data[0]?.subcontractor?.fullName}` : 'PSR-Requests-Export');
  const exportName = csvConfigExport(finalFilename, headersExport);
  const csvExporter = new ExportToCsv(exportName);
  csvExporter.generateCsv(dataExport);
};

export const onExportPSRError = (data: PsrRecordType[] = [], filename?: string) => {
  const dataExport = data?.map((item) => {
    const { response, submissionID, company, subcontractor } = item;
    const payslip = (item as any)?.payload?.payslips?.[0];

    const base = {
      submissionID: submissionID,
      companyID: company?.id || '',
      subcontractorID: subcontractor?.id || '',
      employeeName:
        [payslip?.name?.firstName, payslip?.name?.familyName]
          .filter(Boolean)
          .join(' ') ||
        subcontractor?.fullName ||
        '',
      ppsNumber: payslip?.employeeID?.employeePpsn || '',
      lineItemID: (item as any)?.lineItemID || '',
      status: (item as any)?.revenueStatus || '',
      // Only set on a whole-submission failure, where there are no payslip
      // rows and this one line stands for everyone in the message.
      payslipsAffected: (item as any)?.payslipCount ?? '',
    };

    /**
     * NX-6140 moved where the reason lives.
     *
     * Before batching, each payslip had its own POST and its own rejection,
     * so the error was in response.validationErrors. Now Revenue acknowledges
     * the message and names the bad payslips on the FOLLOW-UP check, and that
     * verdict is stored per row in revenueReason - response only holds the
     * acknowledgement. Reading validationErrors alone exported blank
     * Description, Code and Path columns for every modern rejection.
     *
     * Both are read: the old rows still carry validationErrors and will never
     * gain a revenueReason.
     */
    const revenueReason = (item as any)?.revenueReason;
    if (revenueReason) {
      return { ...base, description: revenueReason, code: '', path: '' };
    }

    if (typeof response === 'string') {
      return { ...base, description: response, code: '', path: '' };
    }

    const { validationErrors } = response ?? ({} as any);
    return {
      ...base,
      description:
        validationErrors?.map((r) => r?.description || '').join('\n') || '',
      code: validationErrors?.map((r) => r?.code || '').join('\n') || '',
      path: validationErrors?.map((r) => r?.path || '').join('\n') || '',
    };
  });
  const headersExport = [
    'submissionID',
    'Employer ID',
    'Employee ID',
    'Employee Name',
    'PPS Number',
    'Line Item ID',
    'Status',
    'Payslips Affected',
    'Description',
    'Code',
    'Path',
  ];
  const finalFilename = filename || (data.length === 1 ? `PSR-Errors-Export-${data[0]?.subcontractor?.id}-${data[0]?.subcontractor?.fullName}` : 'PSR-Errors-Export');
  const exportName = csvConfigExport(finalFilename, headersExport);
  const csvExporter = new ExportToCsv(exportName);
  csvExporter.generateCsv(dataExport);
};

export const handleDownloadJson = (
  jsonData: any,
  type: 'request' | 'response',
) => {
  const element = document.createElement('a');
  const jsonFile = JSON.stringify(jsonData);
  const file = new Blob([jsonFile], { type: 'text/json' });
  element.href = URL.createObjectURL(file);
  element.download = type === 'request' ? 'request.json' : 'response.json';
  document.body.appendChild(element);
  element.click();
};

export const handleExportPSRPayroll = (
  data: PsrRecordType[],
  currencySymbol: string,
) => {
  const dataExport = data?.map((item) => {
    const { payroll, subcontractor, company } = item;
    const {
      irelandValue,
      id,
      otherPayment,
      paymentDate,
      taxPeriod,
      taxYear,
      selfBillRef,
      payFrequency,
      timesheetValue,
      payeeUmbrellaValue,
      otherDeduction,
      net,
    } = payroll;

    return {
      companyID: company?.id || '',
      subcontractorID: subcontractor?.agencyRef || '',
      foreNames: subcontractor?.forename || '',
      surebname: subcontractor?.surname || '',
      taxPeriod: taxPeriod || '',
      taxYear: taxYear || selfBillRef?.taxYear || '',
      payFrequency: payFrequency,
      paymentCompany: company?.companyName || '',
      grossPay: formatMoneyExport(timesheetValue, currencySymbol),
      units: payeeUmbrellaValue?.totalHours || 0,
      salary: formatMoneyExport(irelandValue?.salary || 0, currencySymbol),
      notionalPay: formatMoneyExport(
        irelandValue?.notionalPay || 0,
        currencySymbol,
      ),
      taxableGross: formatMoneyExport(
        irelandValue?.taxGross || 0,
        currencySymbol,
      ),
      taxDeducted: formatMoneyExport(irelandValue?.tax || 0, currencySymbol),
      USCableGross: formatMoneyExport(
        irelandValue.uscGross || 0,
        currencySymbol,
      ),
      USC: formatMoneyExport(irelandValue?.usc || 0, currencySymbol),
      employeePRSIableGross: formatMoneyExport(
        irelandValue?.eePrsiGross || 0,
        currencySymbol,
      ),
      employeePRSI: formatMoneyExport(
        irelandValue?.eePRSI || 0,
        currencySymbol,
      ),
      employerPRSIableGross: formatMoneyExport(
        irelandValue?.erPrsiGross || 0,
        currencySymbol,
      ),
      employerPRSI: formatMoneyExport(
        irelandValue?.erPRSI || 0,
        currencySymbol,
      ),
      pensionableGross: formatMoneyExport(
        irelandValue?.pensionGross || 0,
        currencySymbol,
      ),
      employeePension: formatMoneyExport(
        irelandValue?.eePension,
        currencySymbol,
      ),
      employerPension: formatMoneyExport(
        irelandValue?.erPension,
        currencySymbol,
      ),
      LPT: formatMoneyExport(irelandValue?.lptDeducted || 0, currencySymbol),
      netPay: formatMoneyExport(net, currencySymbol),
      otherPayment: formatMoneyExport(otherPayment, currencySymbol),
      otherDeduction: formatMoneyExport(otherDeduction, currencySymbol),
      sickPay: formatMoneyExport(payeeUmbrellaValue?.ssp, currencySymbol),
      paymentDate: paymentDate
        ? moment(paymentDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      department: subcontractor?.department || '',
      payslipID: id,
    };
  });
  const headersExport = [
    'Employer ID',
    'Employee ID',
    'Forenames',
    'Surname',
    'Tax Period',
    'Tax Year',
    'Pay Frequency',
    'Employer',
    'Gross Pay',
    'Units',
    'Salary',
    'Notional Pay',
    'Taxable Gross',
    'Tax Deducted',
    'USCable Gross',
    'USC',
    'Employee PRSIable Gross',
    'Employee PRSI',
    'Employer PRSIable Gross',
    'Employer PRSI',
    'Pensionable Gross',
    'Employee Pension',
    'Employer Pension',
    'LPT',
    'Net Pay',
    'Other Payment',
    'Other Deduction',
    'Sick Pay (SSP)',
    'Payment Date',
    'Department',
    'Payslip ID',
  ];
  const exportName = csvConfigExport(`PSR-payroll-EXPORT`, headersExport);
  const csvExporter = new ExportToCsv(exportName);
  csvExporter.generateCsv(dataExport);
};

export const calcInsurable = (value: string) => {
  switch (value) {
    case 'WEEKLY':
      return 1;
    case '2_WEEKLY':
      return 2;
    case '4_WEEKLY':
      return 4;
    case 'MONTHLY':
      return 4;
    default:
      return 1;
  }
};
/**
 * The message we sent, with Revenue's verdict added to each payslip.
 *
 * The envelope carries every worker in the run, so finding the one Revenue
 * rejected meant counting array indices and cross-checking the Payslips tab.
 * The verdict is NOT part of what we sent - it is prefixed `_` and listed
 * first so it reads as ours, not as a field Revenue received.
 *
 * Paired on lineItemID, the one identifier Revenue echoes back.
 */
export const buildPsrRequestWithStatus = (detail: any): object => {
  const payload = detail?.requestPayload || {};
  if (!Array.isArray(payload?.payslips)) return payload;

  const rowByLineItem = new Map<string, any>();
  for (const row of detail?.payslips ?? []) {
    if (row?.lineItemID) rowByLineItem.set(row.lineItemID, row);
  }

  return {
    ...payload,
    payslips: payload.payslips.map((payslip: any) => {
      const row = payslip?.lineItemID
        ? rowByLineItem.get(payslip.lineItemID)
        : undefined;
      return {
        _revenueStatus: resolvePsrStatus({
          revenueStatus: row?.revenueStatus,
          batchStatus: detail?.revenueStatus,
          isSuccess: row?.isSuccess,
          isResubmitted: row?.isResubmitted,
          isSuperseded: row?.isSuperseded,
          hasPpsn: Boolean(payslip?.employeeID?.employeePpsn),
        }),
        ...(row?.revenueReason ? { _revenueReason: row.revenueReason } : {}),
        ...payslip,
      };
    }),
  };
};

/**
 * One submission in one of three shapes, shared by the filings list and the
 * submission page so the same row downloads the same bytes from either.
 *
 * Returns false when there is nothing to write, so the caller can say so
 * rather than handing over an empty file.
 */
export const downloadPsrBatch = (
  detail: any,
  type: 'request' | 'response' | 'Payroll',
  currencySymbol: string,
): boolean => {
  if (type === 'request') {
    if (!detail?.requestPayload) return false;
    return (
      handleDownloadJson(buildPsrRequestWithStatus(detail), 'request') ?? true
    );
  }

  if (type === 'response') {
    if (!detail?.response) return false;
    // Revenue's reply is a bare string when the call never got that far, and
    // the downloader would write `"timeout..."` as the whole file.
    handleDownloadJson(
      typeof detail.response === 'string'
        ? { response: detail.response }
        : detail.response,
      'response',
    );
    return true;
  }

  const rows = detail?.payslips ?? [];
  if (!rows.length) return false;
  // The payslip rows carry no company of their own - the detail query joins
  // the subcontractor and the payroll, not the employer - so lend them the
  // batch's, otherwise the Employer column exports empty.
  handleExportPSRPayroll(
    rows.map((row: any) => ({ ...row, company: row.company ?? detail?.company })),
    currencySymbol,
  );
  return true;
};
